import React, { useState } from 'react';
import { ArrowRight, Award, BookOpen, SearchCheck, Building2, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import PricingSection from '../components/PricingSection';
import TestimonialSlider from '../components/TestimonialSlider';
import ProcessSlider from '../components/ProcessSlider';
import MobileSlider from '../components/MobileSlider';
import RegisterPopup from '../components/RegisterPopup';
import SEO from '../components/SEO';
import { services } from '../mock';

const iconMap = {
  Award: Award,
  BookOpen: BookOpen,
  SearchCheck: SearchCheck,
  Building2: Building2,
  ShoppingBag: ShoppingBag
};

const Home = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handlePricingGetStarted = (packageInfo) => {
    setSelectedPackage(packageInfo);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedPackage(null);
  };

  return (
    <div>
      <SEO 
        title="IP Protection Center - Professional Trademark Registration & IP Protection Services USA"
        description="Expert trademark registration services in the USA. Protect your brand with our comprehensive trademark search, filing, and monitoring services. Fast processing, affordable pricing, 98% success rate."
        keywords="trademark registration, trademark services, intellectual property, brand protection, USPTO filing, trademark search, copyright registration, USA trademark"
      />
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(177, 91, 55, 0.7)), url('/assets/img/main-banner-bg.jpg')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Protect Your Brand with
              <span className="text-brand-200"> Professional Trademark Registration</span>
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Secure your business identity with expert trademark services. Fast, reliable, and affordable protection for your brand in the USA.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-aos="fade-up" data-aos="zoom-in">
              <Button
                onClick={() => setShowPopup(true)}
                size="lg"
                className="bg-white text-brand-500 hover:bg-blue50 px-8 py-6 text-lg font-semibold"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-aos="zoom-in" data-aos="fade-up">
            {/* Left Side - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl" data-aos="fade-up" data-aos="fade-up">
                <img
                  src="/assets/img/about-sec.jpg"
                  alt="Professional trademark and brand protection services"
                  className="w-full h-full object-cover"
                  style={{ minHeight: '400px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-900/20 to-transparent" data-aos="zoom-in"></div>
              </div>
              
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-brand-500 rounded-2xl opacity-10 -z-10"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-brand-400 rounded-full opacity-10 -z-10" data-aos="zoom-in"></div>
            </div>

            {/* Right Side - Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Protect Your Brand Identity with Professional Trademark Services
              </h2>
              
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Your brand is more than just a name or logo—it's the foundation of your business identity and reputation. At IP Protection Center, we understand the critical importance of protecting your intellectual property. With over 15 years of experience and thousands of successful trademark registrations, we provide comprehensive protection services that safeguard your brand from infringement and unauthorized use.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Our team of USPTO-accredited specialists combines cutting-edge search technology with deep legal expertise to ensure your trademark application has the highest chance of approval. From initial consultation to final registration and ongoing monitoring, we're with you every step of the way, providing transparent pricing, fast processing, and dedicated support that gives you peace of mind while you focus on growing your business.
              </p>

              <div className="mt-8 flex flex-wrap gap-4" data-aos="fade-up" data-aos="zoom-in">
                <Button
                  onClick={() => setShowPopup(true)}
                  size="lg"
                  className="bg-brand-500 hover:bg-blue770 text-white px-8"
                >
                  Start Your Application
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-brand-500 text-brand-500 hover:bg-brand-50 px-8"
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose IP Protection Center?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              As America's leading trademark service provider, we combine deep legal expertise with personalized client care to deliver superior brand protection results.
            </p>
          </div>

          <MobileSlider itemsPerRow={3} autoSlide={true} slideInterval={4000}>
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-brand-100" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-right">
              <div className="bg-brand-500 rounded-lg w-14 h-14 flex items-center justify-center mb-4" data-aos="zoom-in">
                <Award className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Experienced Legal Team</h3>
              <p className="text-gray-600 mb-3">
                Our trademark attorneys and specialists have over 15 years of combined experience in intellectual property law, ensuring your application is handled with the highest level of expertise.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  USPTO-accredited professionals
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  10,000+ successful registrations
                </li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-brand-100" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-brand-500 rounded-lg w-14 h-14 flex items-center justify-center mb-4" data-aos="zoom-in">
                <SearchCheck className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced Search Technology</h3>
              <p className="text-gray-600 mb-3">
                We utilize cutting-edge search algorithms combined with human expertise to identify potential conflicts across federal, state, and common law databases.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  AI-powered similarity detection
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Phonetic and visual analysis
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-brand-100" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-left">
              <div className="bg-brand-500 rounded-lg w-14 h-14 flex items-center justify-center mb-4" data-aos="zoom-in">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Protection</h3>
              <p className="text-gray-600 mb-3">
                Beyond registration, we offer ongoing monitoring services, opposition support, and renewal management to keep your trademark protected for years to come.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  24/7 infringement monitoring
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Lifetime support included
                </li>
              </ul>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-brand-100" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-brand-500 rounded-lg w-14 h-14 flex items-center justify-center mb-4" data-aos="zoom-in">
                <ShoppingBag className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Pricing</h3>
              <p className="text-gray-600 mb-3">
                No hidden fees, no surprises. Our all-inclusive packages clearly outline what you're paying for, with government fees always disclosed upfront.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Fixed service fees
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Money-back guarantee available
                </li>
              </ul>
            </div>

            {/* Feature 5 */}
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-brand-100" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-brand-500 rounded-lg w-14 h-14 flex items-center justify-center mb-4" data-aos="zoom-in">
                <ArrowRight className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Track Processing</h3>
              <p className="text-gray-600 mb-3">
                We prioritize your application with expedited review and same-day filing options. Most applications are reviewed and filed within 24-48 hours of submission.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Same-day filing available
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Priority customer support
                </li>
              </ul>
            </div>

            {/* Feature 6 */}
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-brand-100" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-brand-500 rounded-lg w-14 h-14 flex items-center justify-center mb-4" data-aos="zoom-in">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Educational Resources</h3>
              <p className="text-gray-600 mb-3">
                Access our extensive library of guides, webinars, and resources to understand trademark law and make informed decisions about your intellectual property.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Free consultation sessions
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                  Comprehensive knowledge base
                </li>
              </ul>
            </div>
          </MobileSlider>

          {/* Call to Action Box */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-brand-500 to-brand-600 rounded-2xl p-8 md:p-12 shadow-2xl" data-aos="zoom-in" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
              <div className="grid md:grid-cols-2 gap-8 items-center" data-aos="zoom-in" data-aos="fade-up" data-aos="zoom-in">
                <div className="text-white">
                  <h3 className="text-3xl font-bold mb-4">Ready to Protect Your Brand?</h3>
                  <p className="text-brand-100 mb-6 text-lg">
                    Join over 10,000 businesses that trust IP Protection Center with their intellectual property protection. Start your trademark journey today with a free consultation.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm" data-aos="fade-up" data-aos="zoom-in">
                    <div className="flex items-center" data-aos="zoom-in">
                      <Award className="h-5 w-5 mr-2" />
                      <span>98% Success Rate</span>
                    </div>
                    <div className="flex items-center" data-aos="zoom-in">
                      <SearchCheck className="h-5 w-5 mr-2" />
                      <span>Expert Support</span>
                    </div>
                    <div className="flex items-center" data-aos="zoom-in">
                      <Building2 className="h-5 w-5 mr-2" />
                      <span>Fast Processing</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-4" data-aos="fade-up">
                  <Button
                    onClick={() => setShowPopup(true)}
                    size="lg"
                    className="bg-white text-brand-500 hover:bg-blue50 w-full text-lg font-semibold py-6"
                  >
                    Get Started Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-brand-100 text-center text-sm">
                    Free consultation • No obligation • Quick response
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-brand-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-aos="fade-up" data-aos="zoom-in">
            <div data-aos="fade-up" data-aos-delay="0" className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">10,000+</div>
              <div className="text-brand-100 text-lg">Trademarks Filed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">98%</div>
              <div className="text-brand-100 text-lg">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">24/7</div>
              <div className="text-brand-100 text-lg">Support Available</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">15+</div>
              <div className="text-brand-100 text-lg">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trademark Protection Matters Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Trademark Protection Matters
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Your brand is your most valuable asset. Here's why federal trademark registration is essential for your business growth.
              </p>
            </div>

            <MobileSlider itemsPerRow={3} autoSlide={true} slideInterval={4000}>
              <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="bg-brand-100 rounded-full w-16 h-16 flex items-center justify-center mb-6" data-aos="zoom-in" data-aos="fade-up">
                  <Award className="h-8 w-8 text-brand-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Exclusive Legal Rights</h3>
                <p className="text-gray-600">
                  Federal registration gives you exclusive nationwide rights to use your trademark, preventing competitors from copying your brand identity.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="bg-brand-100 rounded-full w-16 h-16 flex items-center justify-center mb-6" data-aos="zoom-in" data-aos="fade-up">
                  <SearchCheck className="h-8 w-8 text-brand-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Brand Value & Credibility</h3>
                <p className="text-gray-600">
                  A registered trademark increases your business value, builds customer trust, and demonstrates professional credibility in your market.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="bg-brand-100 rounded-full w-16 h-16 flex items-center justify-center mb-6" data-aos="zoom-in" data-aos="fade-up">
                  <Building2 className="h-8 w-8 text-brand-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Growth Protection</h3>
                <p className="text-gray-600">
                  Protect your investment as you grow. Trademarks are essential for expansion, franchising, licensing, and attracting investors.
                </p>
              </div>
            </MobileSlider>

            <div className="mt-12 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-6" data-aos="zoom-in" data-aos="fade-up">
              <div className="flex items-start" data-aos="zoom-in">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-lg font-semibold text-amber-900 mb-1">Don't Wait Until It's Too Late</h4>
                  <p className="text-amber-800">
                    Without trademark protection, competitors can use similar names and logos, confusing your customers and damaging your reputation. Register today to secure your brand before someone else does.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Comprehensive Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete trademark and intellectual property solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-aos="fade-up" data-aos="zoom-in">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all cursor-pointer group"
                >
                  <div className="bg-brand-100 rounded-full w-12 h-12 flex items-center justify-center mb-4 group-hover:bg-brand-500 transition-colors" data-aos="zoom-in" data-aos="fade-up">
                    <Icon className="h-6 w-6 text-brand-500 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Button
                    variant="link"
                    className="text-brand-500 hover:text-brand-600 p-0"
                  >
                    Learn More <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            
            {/* CTA Box */}
            <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all text-white flex flex-col justify-between" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
              <div>
                <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-aos="zoom-in" data-aos="fade-up">
                  <ArrowRight className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Need Expert Guidance?</h3>
                <p className="text-white/90 mb-4">
                  Not sure which service is right for you? Our trademark experts are ready to help you choose the best solution for your brand protection needs.
                </p>
              </div>
              <Button
                onClick={() => setShowPopup(true)}
                className="bg-white text-brand-500 hover:bg-blue50 w-full"
              >
                Get Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <ProcessSlider />

      {/* Pricing Section */}
      <PricingSection onGetStarted={handlePricingGetStarted} />

      {/* Testimonials Section */}
      <TestimonialSlider />

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Get answers to common questions about trademark registration
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-aos="fade-up">
            {/* Left Side - FAQ Content */}
            <div className="space-y-4">
              {/* FAQ 1 */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden" data-aos="fade-up">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      How long does trademark registration take?
                    </h3>
                    <span className="text-brand-500 group-open:rotate-180 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      The complete trademark registration process typically takes 8-12 months from filing to approval. We handle all communications with the USPTO and keep you updated throughout the entire process. Most applications receive an initial examination within 3-4 months.
                    </p>
                  </div>
                </details>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      What's included in your trademark services?
                    </h3>
                    <span className="text-brand-500 group-open:rotate-180 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      Our services include comprehensive trademark search, application preparation, USPTO filing, office action responses, and ongoing monitoring. Premium packages include dedicated case managers, expedited processing, and cease & desist letter preparation.
                    </p>
                  </div>
                </details>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      Do I need a trademark if I have an LLC?
                    </h3>
                    <span className="text-brand-500 group-open:rotate-180 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      Yes. Forming an LLC protects your personal assets, but it doesn't protect your brand name nationwide. A federal trademark registration gives you exclusive rights to use your brand name across the United States in your industry.
                    </p>
                  </div>
                </details>
              </div>

              {/* FAQ 4 */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      What happens if my application is rejected?
                    </h3>
                    <span className="text-brand-500 group-open:rotate-180 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      Our comprehensive search service minimizes rejection risk. If an office action is issued, our experienced team will work with you to craft an appropriate response. Premium packages include additional support for handling USPTO objections.
                    </p>
                  </div>
                </details>
              </div>

              {/* FAQ 5 */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      How much does trademark registration cost?
                    </h3>
                    <span className="text-brand-500 group-open:rotate-180 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      Our service fees range from $45 to $349 depending on the package you choose. Government filing fees ($250-$350 per class) are separate. We provide transparent pricing with no hidden costs, and you'll know the exact total before proceeding.
                    </p>
                  </div>
                </details>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Button
                  onClick={() => setShowPopup(true)}
                  className="w-full bg-brand-500 hover:bg-blue770 text-white py-6 text-lg"
                >
                  Have More Questions? Contact Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="relative hidden md:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-aos="fade-up">
                <img
                  src="/assets/img/home-sec.jpg"
                  alt="Professional business consultation"
                  className="w-full h-full object-cover"
                  style={{ minHeight: '600px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-aos="zoom-in"></div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white rounded-xl p-6 shadow-xl" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="grid grid-cols-2 gap-4" data-aos="fade-up" data-aos="zoom-in">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-brand-500">98%</div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-brand-500">10K+</div>
                    <div className="text-sm text-gray-600">Clients Served</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Don't wait to secure your trademark. Get started today and protect your business identity with our expert services.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-brand-500 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Register My Trademark Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={handleClosePopup} packageInfo={selectedPackage} />}
    </div>
  );
};

export default Home;